<script setup lang="ts">
import { useMenuRowActionKeys } from '@/composables/useMenuRowActionKeys';
import { computed, ref } from 'vue';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StarIcon as StarIconOutline } from '@heroicons/vue/24/outline';
import { ArrowPathIcon, XMarkIcon } from '@heroicons/vue/24/solid';
import type { TableColumn } from '@/types/tableTypes';
import { useFavoriteFilters } from '@/composables/useFavoriteFilters';
import { isOptimisticFavoriteId } from '@/composables/api/useFavoriteFiltersApi';
import {
  favoriteRuleColumnKey,
  type FavoriteFilter,
  type FavoriteRuleSnapshot,
} from '@/utils/favoriteFilters';

/**
 * The "Add Fav" menu (SAU-260). Always visible next to "+ Add Filter" — if it
 * only appeared after starring something, nobody would ever discover it — and
 * always a menu (it doubles as the only place to remove favorites without
 * rebuilding the exact row). Clicking an item emits `insert`; the host appends
 * the pre-filled row. Inserting never applies — Apply stays the gate.
 */

const props = defineProps<{
  /** The page's filterable columns — favorites whose column is gone render disabled. */
  columns: TableColumn[];
}>();

const emit = defineEmits<{
  insert: [rule: FavoriteRuleSnapshot];
}>();

const menuOpen = ref(false);

const { favorites, removeFavorite, isLoading, isError, refetch } = useFavoriteFilters();

const availableColumnKeys = computed(() => new Set(props.columns.map((column) => column.key)));

/**
 * Stale-column guard: schemas change and DB rows outlive deploys, so a
 * favorite may reference a column this page no longer has. It renders
 * disabled (never insertable as a broken row) but stays removable.
 */
function isColumnAvailable(favorite: FavoriteFilter): boolean {
  return availableColumnKeys.value.has(favoriteRuleColumnKey(favorite.rule));
}

function favoriteItemTitle(favorite: FavoriteFilter): string {
  if (!isColumnAvailable(favorite)) {
    return `"${favorite.label}" uses a column that isn't available on this page, so it can't be inserted here. It can still be removed.`;
  }
  return favorite.label;
}

function onItemSelect(favorite: FavoriteFilter, event: Event) {
  // Take over close handling from the dropdown: stale items and the remove X
  // must never half-close the menu or insert as a side effect.
  event.preventDefault();
  if (!isColumnAvailable(favorite)) {
    return;
  }
  emit('insert', favorite.rule);
  menuOpen.value = false;
}

function onRemove(favorite: FavoriteFilter) {
  // The menu stays open while pruning (removal never also inserts — the
  // X stops propagation so the item's select never fires).
  if (isOptimisticFavoriteId(favorite.id)) {
    // Still being created — no server id to DELETE yet (the X renders
    // disabled during that window; this is belt-and-braces).
    return;
  }
  void removeFavorite(favorite.id);
}

// Keyboard access to the remove button nested in each row — the same gap the
// saved-views menu had, fixed the same way. See useMenuRowActionKeys.
const { onRowKeydown, onActionKeydown } = useMenuRowActionKeys();

function removeLabel(favorite: { id: number; label: string }): string {
  return isOptimisticFavoriteId(favorite.id) ? 'Saving favorite…' : `Remove favorite "${favorite.label}"`;
}
</script>

<template>
  <DropdownMenu v-model:open="menuOpen">
    <DropdownMenuTrigger as-child>
      <Button
        variant="link"
        class="h-6 text-xs px-1 gap-1 text-muted-foreground hover:text-foreground hover:no-underline whitespace-nowrap"
        type="button"
        data-testid="filter-add-favorite-button"
      >
        <StarIconOutline class="h-3.5 w-3.5" />
        Add Fav
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" class="w-64">
      <div
        v-if="isLoading"
        class="px-2 py-3 text-xs text-muted-foreground"
        data-testid="favorite-filters-loading-state"
      >
        Loading favorites…
      </div>
      <div
        v-else-if="isError"
        class="flex flex-col items-start gap-1 px-2 py-2"
        data-testid="favorite-filters-error-state"
      >
        <span class="text-xs text-muted-foreground">Couldn't load favorites.</span>
        <!--
          A real menu item, not a bare Button: reka's arrow navigation only
          walks menu items, so as a Button this was reachable by mouse alone —
          the one control on the one screen where the menu has nothing else to
          offer. `@select.prevent` keeps the menu open for the retry.
        -->
        <DropdownMenuItem
          class="h-6 gap-1 px-1 text-xs"
          data-testid="favorite-filters-retry-button"
          @select.prevent="refetch()"
        >
          <ArrowPathIcon class="h-3.5 w-3.5" />
          Retry
        </DropdownMenuItem>
      </div>
      <div
        v-else-if="favorites.length === 0"
        class="flex flex-col items-center gap-1 px-3 py-4 text-center"
        data-testid="favorite-filters-empty-state"
      >
        <StarIconOutline class="h-5 w-5 text-muted-foreground/70" />
        <span class="text-xs font-medium">No favorites yet</span>
        <span class="text-xs text-muted-foreground">Star a filter row to save it here.</span>
      </div>
      <template v-else>
        <DropdownMenuItem
          v-for="favorite in favorites"
          :key="favorite.id"
          class="flex items-center justify-between gap-2 text-xs focus-within:bg-accent focus-within:text-accent-foreground"
          :data-testid="`favorite-filter-item-${favorite.id}`"
          :data-stale="!isColumnAvailable(favorite) ? 'true' : undefined"
          :title="favoriteItemTitle(favorite)"
          @keydown="onRowKeydown"
          @select="onItemSelect(favorite, $event)"
        >
          <span
            class="min-w-0 truncate"
            :class="!isColumnAvailable(favorite) && 'opacity-50'"
          >{{ favorite.label }}</span>
          <Button
            size="icon"
            variant="ghost"
            class="shrink-0 h-5 w-5 text-muted-foreground hover:text-destructive"
            type="button"
            tabindex="-1"
            :disabled="isOptimisticFavoriteId(favorite.id)"
            :aria-label="removeLabel(favorite)"
            :title="removeLabel(favorite)"
            data-testid="favorite-filter-remove-button"
            data-menu-row-action="remove"
            @click.stop="onRemove(favorite)"
            @keydown="onActionKeydown"
          >
            <XMarkIcon class="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuItem>
      </template>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
